import { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { Box } from '@mui/material'

import { getRole } from 'src/service/authService'
import InputRHF from '../../../../components/RHF/InputRHF'
import { FooterBottons } from 'src/components/FormUtils/FooterButtons'
import { goToApiKeys } from 'src/routes/coordinator'
import { CompanySelect } from 'src/components/CompanySelect/CompanySelect'
import { Company } from 'src/models/company-model'
import { useGlobalSelectedCompany } from 'src/context/GlobalSelectedCompanyContext'
import { apiKeyDefaultValues, apiKeySchemaValidation } from '../../validators/apiKeysSchemaValidation'
import InputSelectRHF from 'src/components/RHF/InputSelectRHF'

interface ApiKeyFormProps {
  onSubmit: (data) => void
}

type ExpirationOption = {
  id: number
  label: string
  value: string
  days?: number // undefined = "sem limite"
}

export const ApiKeyForm = ({ onSubmit }: ApiKeyFormProps) => {
  const { t } = useTranslation()
  const { id } = useParams()
  const { isGlobalAdminSelected, selectedCompanyData } = useGlobalSelectedCompany()
  const form = useForm<any>({
    mode: 'all',
    defaultValues: apiKeyDefaultValues,
    resolver: yupResolver(apiKeySchemaValidation),
  })
  const { control, handleSubmit, reset, formState, setValue } = form

  const expirationOptions: ExpirationOption[] = [
    { id: 1, label: t('apikey.one.day'), value: '1d', days: 1 },
    { id: 2, label: t('apikey.seven.days'), value: '7d', days: 7 },
    { id: 3, label: t('apikey.fifteen.days'), value: '15d', days: 15 },
    { id: 4, label: t('apikey.thirty.days'), value: '30d', days: 30 },
    { id: 5, label: t('apikey.hundred.days'), value: '100d', days: 100 },
    { id: 6, label: t('apikey.unlimited'), value: 'unlimited' },
  ]

  const onChangeCompany = (company: Company) => {
    setValue('companyId', company)
  }

  const resetForm = () => {
    reset({ groupName: '' })
  }

  useEffect(() => {
    if (!isGlobalAdminSelected()) {
      onChangeCompany(selectedCompanyData as Company)
    }
    if (!isGlobalAdminSelected && !selectedCompanyData) {
      setValue('companyId', undefined)
    }
  }, [selectedCompanyData])

  return (
    <Box onSubmit={handleSubmit(onSubmit)} component="form">
      <Box>
        {getRole() === 'TECNOIT' && (
          <CompanySelect
            control={control}
            onChangeAction={onChangeCompany}
          />
        )}
        <Box
          sx={{
            display: 'grid',
            mt: '28.5px',
            mb: '28px',
            gridColumn: {
              xs: 'span 12',
              sm: 'span 12',
              md: 'span 12',
              lg: 'span 12',
            },
          }}
        >
          <InputRHF
            required
            control={control}
            label={t('apikey.form.input.label.name')}
            name="name"
            placeholder={t('apikey.form.input.placeholder.name')}
            sx={{
              gridColumn: 'span 11',
              mb: '11px',
            }}
          />
        </Box>
        <InputSelectRHF
          options={expirationOptions}
          control={control}
          value=''
          label={t('apikey.form.input.label.expiration')}
          name="expiresAt"
          sx={{
            gridColumn: {
              xs: 'span 10',
              sm: 'span 4',
              md: 'span 4',
              lg: 'span 4',
            },
            mb: '11px',
          }}
          isOptionEqualToValueFn={(option, value) => option.id === value.id}
          getOptionLabel={(option) => option?.label || ''}
        />
      </Box>
      <FooterBottons
        isValid={!formState.isValid}
        formIsValid={formState.isValid}
        isEdit={Boolean(id)}
        goTo={goToApiKeys}
        reset={() => resetForm()}
        size="100%"
        buttonName="apikey.button.create"
      />
    </Box>
  )
}
